<script setup>
import { Head } from '@inertiajs/vue3';
import { useItemStore } from '@/stores/item';
import { useKaleidicalStore } from '@/stores/kaleidical';
import { storeToRefs } from 'pinia';
import { onMounted, ref, toRaw, watch, computed } from 'vue';
import { useToast } from 'vue-toastification';
import { router } from '@inertiajs/vue3';
import { useDateFormatter } from '@/composables/dateFormatter';
import * as d3 from 'd3';
import * as d3hexbin from 'd3-hexbin';

const { formatDate } = useDateFormatter();

const { kaleidicalItems } = storeToRefs(useItemStore());
const { active: kaleidical } = storeToRefs(useKaleidicalStore());
const { isGuestAccessing } = storeToRefs(useKaleidicalStore());

const props = defineProps({
    items: {
        type: Array,
        required: true,
        default: () => [],
    },
});

const label = ref('');
const { updateItemsQuantity, updateItem, removeItem } = useItemStore();

const toast = useToast();

const zoomOutAllTheWay = () => {
    try {
        const svg = d3.select('#kale');
        const zoom = d3.zoom(); // Create a new zoom behavior
        svg.transition()
            .duration(750)
            .call(zoom.transform, d3.zoomIdentity.scale(1));
    } catch (error) {
        //console.log(error);
    }
};

const update = async () => {
    try {
        await updateItemsQuantity({
            items: kaleidicalItemsByDate.value,
            hash: kaleidical.value.hash,
            date: currentDate.value
        });

        zoomOutAllTheWay();
    } catch (error) {
        toast.error('Sorry we could not update the Kaledical',);
    }
};

const loadKaleidicalsPage = () => {
    router.visit('/');
};

const add = (index) => {
    kaleidicalItemsByDate.value[index].quantity = Math.max(0, kaleidicalItemsByDate.value[index].quantity + 1);
    update();
};

const minus = (index) => {
    kaleidicalItemsByDate.value[index].quantity = Math.max(0, kaleidicalItemsByDate.value[index].quantity - 1);
    update();
};

const save = (color, index) => {
    kaleidicalItemsByDate.value[index].color = color;
    update();
};

const currentDate = ref(new Date);

import { addDays } from 'date-fns';

const increaseDate = () => {
    currentDate.value = addDays(currentDate.value, 1);
};

const decreaseDate = () => {
    currentDate.value = addDays(currentDate.value, -1);
};

const build = (items, clear = false) => {
    let d3Hexbin = d3hexbin.hexbin();

    let formatHexes = [];
    items.forEach((item) => {
        item.quantities.forEach((quantity) => {
            for (let i = 0; i < quantity.quantity; i++) {
                let dateString = quantity.created_on;

                formatHexes.push({
                    color: item.color,
                    date: dateString.slice(5, 7) + '/' + dateString.slice(8, 10),
                });
            }
        });
    });

    let sortHexesByDate = formatHexes.sort((a, b) => {
        const [monthA, dayA] = a.date.split('/');
        const [monthB, dayB] = b.date.split('/');

        // Compare by month first, and then by day if the months are the same
        if (monthA === monthB) {
            return dayA - dayB;
        }
        return monthA - monthB;
    });

    let hexes = sortHexesByDate;

    let count = hexes.length;

    const width = document.querySelector('#kale-wrapper').offsetWidth;
    const height = 500;
    // Create an SVG element
    const svg = d3.select('#kale')
        .attr('viewBox', `0 0 ${width} ${height}`)
        .attr('preserveAspectRatio', 'xMidYMid meet');;
    if (clear) {
        svg.selectAll('*').remove();
    }

    // Define the dimensions of the hexagon
    let hexRadius = 15;

    if (count >= 100 && count < 250) {
        hexRadius = 12;
    } else if (count >= 250 && count < 400) {
        hexRadius = 10;
    } else if (count >= 400 && count < 600) {
        hexRadius = 8;
    } else if (count >= 600 && count < 800) {
        hexRadius = 5;
    } else if (count >= 800 && count < 1000) {
        hexRadius = 3;
    } else {
        hexRadius = 25;
    }

    // Create the hexbin generator
    const hexbin = d3Hexbin
        .radius(hexRadius);

    // Center hexagon (placed in the middle)
    const centerX = width / 2;
    const centerY = (height / 2);

    // Directions for hexagon movement: [x offset, y offset]
    const directions = [
        [1, 0], // Right
        [0.5, Math.sqrt(3) / 2], // Bottom right
        [-0.5, Math.sqrt(3) / 2], // Bottom left
        [-1, 0], // Left
        [-0.5, -Math.sqrt(3) / 2], // Top left
        [0.5, -Math.sqrt(3) / 2]  // Top right
    ];

    // Function to generate a spiral of hexagons
    function generateSpiralHexagons(hexes) {
        const points = [];

        // Add the center hexagon
        points.push([centerX, centerY]);

        // Keep track of how many hexagons we have added
        let currentHexCount = 1;

        // Loop through layers and add hexagons in a spiral
        let layer = 1;
        hexes.forEach((_) => {
            let x = centerX + directions[4][0] * layer * hexRadius * Math.sqrt(3);
            let y = centerY + directions[4][1] * layer * hexRadius * Math.sqrt(3);

            // Traverse each direction to place hexagons around the previous layer
            for (let direction = 0; direction < 6; direction++) {
                for (let i = 0; i < layer; i++) {
                    if (currentHexCount >= hexes.length + 1) {
                        return points;  // Stop when we reach the desired count
                    }

                    points.push([x, y, {
                        layer: layer,
                        direction: direction,
                        index: currentHexCount,
                        color: hexes[currentHexCount - 1]?.color ?? null,
                        date: hexes[currentHexCount - 1]?.date,
                    }]);

                    currentHexCount++;

                    // Move to the next position in the current direction
                    x += directions[direction][0] * hexRadius * Math.sqrt(3);
                    y += directions[direction][1] * hexRadius * Math.sqrt(3);
                }
            }

            layer++;  // Move to the next layer
        });

        return points;
    }

    let hexPoints = generateSpiralHexagons(hexes);

    // Create a group for hexagons and labels
    const g = svg.append('g');

    // Zoom behavior
    const zoom = d3.zoom()
        .scaleExtent([1, 10])
        .on('zoom', (event) => {
            g.attr('transform', event.transform);

            // Check zoom scale
            const zoomScale = event.transform.k;

            // Show text only if zoomed in to scale 10
            if (zoomScale >= 3) {
                g.selectAll('.hex-label').style('visibility', 'visible');
            } else {
                g.selectAll('.hex-label').style('visibility', 'hidden');
            }
        });

    svg.call(zoom);

    // Draw hexagons
    g.append('g')
        .selectAll('.hexagon')
        .data(hexPoints)
        .enter().append('path')
        .attr('d', hexbin.hexagon())  // generate hexagon path
        .attr('transform', d => `translate(${d[0]}, ${d[1]})`)
        .attr('stroke', '#000')  // Optional: set stroke color
        .attr('stroke-width', 1)  // Optional: set stroke width
        .attr('fill', (d) => {
            return d?.[2]?.color ?? '#5d336e'; // Set center fill color
        })
        .style('opacity', 0)  // Initially hide the hexagons
        .transition()  // Start the transition
        .delay((d, i) => i * (hexes.length > 1 ? 1500 / (hexes.length - 1) : 0))
        .duration(500)  // Set the animation duration (500ms for each hexagon)
        .style('opacity', 1);  // Fade in the hexagons

    let fontSize = '7px';
    if (hexes.length > 200) {
        fontSize = '5px';
    }
    if (hexes.length > 400) {
        fontSize = '3px';
    }

    if (hexes.length > 600) {
        fontSize = '2px';
    }

    g.append('g')
        .selectAll('.hex-label')
        .data(hexPoints)
        .enter()
        .append('text')
        .attr('class', 'hex-label')
        .attr('x', d => d[0])  // Set x to the hexagon's center
        .attr('y', d => d[1])  // Set y to the hexagon's center
        .attr('text-anchor', 'middle')  // Center the text
        .style('opacity', 0)  // Initially hidden
        .style('visibility', 'hidden')  // Initially hidden
        .style('font-size', fontSize)  // Initially hidden
        .text(d => d?.[2]?.date ?? null)
        .transition()  // Start transition for the initial appearance
        .delay((d, i) => (i * (hexes.length > 100 ? 100 : 200)) + 100)   // Delay each hexagon by 100ms (adjust as needed)
        .duration(1000)  // Set the duration of the animation
        .style('opacity', 0.8);  // Fade in the text
};

onMounted(() => {
    let val = toRaw(props.items);
    if (val) {
        build(val);
    }
});

watch(kaleidicalItems, (val) => {
    if (val) {
        build(toRaw(val), true);
    }
});

const checkInput = (index) => {
    //check to make sure its not negative
    if (kaleidicalItemsByDate.value[index].quantity < 0 || kaleidicalItemsByDate.value[index].quantity > 200) {
        kaleidicalItemsByDate.value[index].quantity = 0;
    } else {
        update();
    }
};

const { shareKaleidical } = useItemStore();

const share = (social) => {
    shareKaleidical({
        platform: social,
        hash: kaleidical.value.hash
    }).then((response) => {
        if (social == 'link') {
            const url = response.data.url;
            const tempInput = document.createElement('input');
            tempInput.value = url;
            document.body.appendChild(tempInput);
            tempInput.select();
            document.execCommand('copy');
            document.body.removeChild(tempInput);
            toast.success('Shared link copied to clipboard');
            return;
        }
        window.location.href = response.data.url;
    }).catch((error) => {
        console.error('Error sharing:', error);
    });
};

const { store } = useItemStore();

const addNewItem = async () => {
    await store({
        name: label.value,
        hash: kaleidical.value.hash
    });

    label.value = '';
};

const kaleidicalItemsByDate = computed(() => {
    return kaleidicalItems.value.map(x => {
        return {
            ...x,
            quantity: toRaw(x.quantities)
                .filter(y => y.created_on === formatDate(currentDate.value, 'yyyy-MM-dd'))
                .flatMap(x => x.quantity).reduce((a, b) => a + b, 0) ?? 0
        };
    });
});

const editMode = ref(false);
const itemLabel = ref('');
const editIndex = ref(null);

const enableEditMode = (name, index) => {
    itemLabel.value = name;
    editMode.value = true;
    editIndex.value = index;
};

const saveItem = (item, index) => {
    updateItem({
        hash: item.hash,
        name: itemLabel.value
    }).then(() => {
        kaleidicalItemsByDate.value[index].name = itemLabel.value;
        editMode.value = false;
        itemLabel.value = '';
        editIndex.value = null;
    });
};

const deleteItem = (item) => {
    removeItem({
        hash: item.hash,
    }).then((_) => {
        editIndex.value = null;
        editMode.value = false;
    });
};

</script>
<template>
<MainContentLayout>
    <Head title="Kaleidical Items" />
    <main class="w-full relative">
        <div class="flex justify-between items-center px-4 absolute top-5 left-0" style="width: 100px;">
            <h1 v-if="!isGuestAccessing" class="hover:text-primary-light text-lg flex gap-4 items-center cursor-pointer" @click="loadKaleidicalsPage()"><FontAwesomeIcon icon="chevron-left" />Back</h1>
        </div>

        <div class="max-w-4xl mx-auto grid grid-cols-1">
            <div id="kale-wrapper">
                <svg
                    id="kale"
                    width="100%"
                    height="100%">
                    t
                </svg>
            </div>

            <div class="bg-white min-h-[85px]">
                <div class="text-center text-lg font-bold mt-4 flex gap-4 justify-center items-center">
                    <FontAwesomeIcon
                        icon="chevron-left"
                        class="text-primary-light font-bold hover:text-primary cursor-pointer"
                        @click="decreaseDate" />
                    <div class="w-full">{{ formatDate(currentDate, 'E do LLL, yy') }}</div>
                    <FontAwesomeIcon
                        icon="chevron-right"
                        class="text-primary-light font-bold hover:text-primary cursor-pointer"
                        @click="increaseDate" />
                </div>
                <div v-if="kaleidicalItems?.length" class="flex flex-col pb-4 items-center rounded-tr-lg rounded-tl-lg">
                    <div
                        v-for="(item, index) in kaleidicalItemsByDate"
                        :key="index"
                        class="mt-4 flex flex-row items-center justify-center w-full"
                        :class="{ '-mt-0.5' : editIndex !== index }">
                        <div class="w-1/2 gap-4 flex">
                            <div class="w-3" :class="{ 'mt-1' : editIndex === index }">
                                <color-picker
                                    v-model="item.color"
                                    shape="circle"
                                    format="hex8"
                                    :pure-color="item.color"
                                    @update:pureColor="save($event, index)" />
                            </div>
                            <span v-if="editIndex !== index" class="truncate" @click="enableEditMode(item.name, index)">{{ item.name }}</span>

                            <template v-if="editMode && editIndex === index">
                                <div class="flex gap-2">
                                    <input
                                        v-model="itemLabel"
                                        style="border: 1px solid #444"
                                        class="w-60 border-2 border-grey-500 h-7 mt-1 rounded p-1">
                                    <div class="rounded-full h-7 w-7 flex items-center cursor-pointer mt-1 justify-center bg-green-300 hover:bg-primary-light z-10" @click="saveItem(item, index)">
                                        <FontAwesomeIcon icon="save" class="text-base cursor-pointer" />
                                    </div>
                                    <div class="rounded-full h-7 w-7 flex items-center cursor-pointer mt-1 justify-center bg-secondary hover:bg-primary-light z-10" @click="deleteItem(item)">
                                        <FontAwesomeIcon icon="times" class="text-base cursor-pointer" />
                                    </div>
                                </div>
                            </template>
                        </div>

                        <button
                            v-if="!isGuestAccessing"
                            type="button"
                            :disabled="item.quantity === 0"
                            class="w-5 h-5 bg-primary text-white disabled:bg-grey-200 hover:bg-primary-light rounded-full text-sm font-medium"
                            @click="minus(index)">
                            <FontAwesomeIcon
                                icon="minus"
                                class="text-lg" />
                        </button>
                        <div class="rounded-lg border border-black mx-2">
                            <input
                                id="number"
                                v-model="item.quantity"
                                :disabled="isGuestAccessing"
                                type="text"
                                min="0"
                                class="bg-gray-50 text-gray-900 rounded-lg p-2 text-center font-bold text-base w-20 h-8"
                                :placeholder="item.quantity"
                                @input="checkInput(index)">
                        </div>
                        <button
                            v-if="!isGuestAccessing"
                            type="button"
                            class="w-5 h-5 bg-primary text-white hover:bg-primary-light rounded-full text-sm font-medium"
                            @click="add(index)">
                            <FontAwesomeIcon
                                icon="plus"
                                class="text-lg" />
                        </button>

                    </div>
                </div>
                <div v-else>
                    <p class="text-center italic py-3">No Items. Please Add One.</p>
                </div>
            </div>
            <div v-if="!isGuestAccessing" class="px-8 md:px-0 mt-4 flex flex-row items-center justify-center w-full bg-white pt-4 gap-12 rounded-br-lg rounded-bl-lg">
                <div class="w-full md:w-1/2">
                    <StyledInput
                        v-model="label"
                        class="w-full mt-1"
                        label="Label"
                        name="label" />
                </div>
                <StyledButton :disabled="!label.length" class="mb-5 w-24" @click="addNewItem">Add <FontAwesomeIcon class="ml-2" icon="plus" /></StyledButton>
            </div>
        </div>

        <div v-if="!isGuestAccessing" class="flex items-center justify-center">
            <!--MODAL ITEM-->
            <div class="w-full mx-4 p-4 rounded-xl">
                <!--MODAL BODY-->
                <div class="mt-4">
                    <div class="flex gap-4 justify-around my-4">
                        <!--FACEBOOK ICON-->
                        <div
                            class="bg-grey-100 hover:bg-[#1877f2] w-12 h-12 fill-[#1877f2] hover:fill-white rounded-full flex items-center justify-center shadow-xl hover:shadow-blue-500/50 cursor-pointer"
                            @click="share('facebook')">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24">
                                <path
                                    d="M13.397 20.997v-8.196h2.765l.411-3.209h-3.176V7.548c0-.926.258-1.56 1.587-1.56h1.684V3.127A22.336 22.336 0 0 0 14.201 3c-2.444 0-4.122 1.492-4.122 4.231v2.355H7.332v3.209h2.753v8.202h3.312z" />
                            </svg>

                        </div>
                        <!--TWITTER ICON-->
                        <div
                            class="bg-grey-100 border hover:bg-[#1d9bf0] w-12 h-12 fill-[#1d9bf0] hover:fill-white border-blue-200 rounded-full flex items-center justify-center shadow-xl hover:shadow-sky-500/50 cursor-pointer"
                            @click="share('twitter')">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24">
                                <path
                                    d="M19.633 7.997c.013.175.013.349.013.523 0 5.325-4.053 11.461-11.46 11.461-2.282 0-4.402-.661-6.186-1.809.324.037.636.05.973.05a8.07 8.07 0 0 0 5.001-1.721 4.036 4.036 0 0 1-3.767-2.793c.249.037.499.062.761.062.361 0 .724-.05 1.061-.137a4.027 4.027 0 0 1-3.23-3.953v-.05c.537.299 1.16.486 1.82.511a4.022 4.022 0 0 1-1.796-3.354c0-.748.199-1.434.548-2.032a11.457 11.457 0 0 0 8.306 4.215c-.062-.3-.1-.611-.1-.923a4.026 4.026 0 0 1 4.028-4.028c1.16 0 2.207.486 2.943 1.272a7.957 7.957 0 0 0 2.556-.973 4.02 4.02 0 0 1-1.771 2.22 8.073 8.073 0 0 0 2.319-.624 8.645 8.645 0 0 1-2.019 2.083z" />
                            </svg>
                        </div>
                        <!--WHATSAPP ICON-->
                        <div
                            class="bg-grey-100 border hover:bg-[#25D366] w-12 h-12 fill-[#25D366] hover:fill-white border-green-200 rounded-full flex items-center justify-center shadow-xl hover:shadow-green-500/50 cursor-pointer"
                            @click="share('whatsapp')">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24">
                                <path
                                    fill-rule="evenodd"
                                    clip-rule="evenodd"
                                    d="M18.403 5.633A8.919 8.919 0 0 0 12.053 3c-4.948 0-8.976 4.027-8.978 8.977 0 1.582.413 3.126 1.198 4.488L3 21.116l4.759-1.249a8.981 8.981 0 0 0 4.29 1.093h.004c4.947 0 8.975-4.027 8.977-8.977a8.926 8.926 0 0 0-2.627-6.35m-6.35 13.812h-.003a7.446 7.446 0 0 1-3.798-1.041l-.272-.162-2.824.741.753-2.753-.177-.282a7.448 7.448 0 0 1-1.141-3.971c.002-4.114 3.349-7.461 7.465-7.461a7.413 7.413 0 0 1 5.275 2.188 7.42 7.42 0 0 1 2.183 5.279c-.002 4.114-3.349 7.462-7.461 7.462m4.093-5.589c-.225-.113-1.327-.655-1.533-.73-.205-.075-.354-.112-.504.112s-.58.729-.711.879-.262.168-.486.056-.947-.349-1.804-1.113c-.667-.595-1.117-1.329-1.248-1.554s-.014-.346.099-.458c.101-.1.224-.262.336-.393.112-.131.149-.224.224-.374s.038-.281-.019-.393c-.056-.113-.505-1.217-.692-1.666-.181-.435-.366-.377-.504-.383a9.65 9.65 0 0 0-.429-.008.826.826 0 0 0-.599.28c-.206.225-.785.767-.785 1.871s.804 2.171.916 2.321c.112.15 1.582 2.415 3.832 3.387.536.231.954.369 1.279.473.537.171 1.026.146 1.413.089.431-.064 1.327-.542 1.514-1.066.187-.524.187-.973.131-1.067-.056-.094-.207-.151-.43-.263" />
                            </svg>
                        </div>
                        <div
                            class="bg-grey-100 border hover:bg-[#1877f2] w-12 h-12 fill-[#1877f2] text-blue hover:text-white border-green-200 rounded-full flex items-center justify-center shadow-xl hover:shadow-green-500/50 cursor-pointer"
                            @click="share('link')">
                            Link
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </main>
</MainContentLayout>
</template>

<style scoped>
.hex-label  {
    fill: #fff;
}

.hex-label {
    text-anchor: middle;
    font-size: 8px;
}

.hexagon {
    transition: transform 0.3s ease; /* Smooth transition for transform */
    transform-origin: center; /* Ensure scaling happens from the center */
}

.hexagon:hover {
    transform: scale(1.5); /* Increase size on hover */
}

.svg-container {
    display: flex;
    justify-content: center;    /* Center horizontally */
    align-items: center;        /* Center vertically within its container */
    width: 100%;                /* Full width */
}

#kale-wrapper {
    width: calc(100% - 100px);
    height: auto;               /* Allow height to adjust based on width */
    max-height: 500px;          /* Set maximum height */
    aspect-ratio: 1;            /* Ensure the aspect ratio is maintained */
    margin: 0 auto;
}

svg {
    display: block;
    max-height: 500px;          /* Limit the SVG's height to 500px */
    width: 100%;                /* Full width for responsiveness */
}
</style>
